import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Card,
  Typography,
  Space,
  Tag,
  Spin,
  message,
  Descriptions,
  Row,
  Col,
  Table,
  Collapse,
  Divider,
  Button,
} from "antd";
import {
  HEADERS_EXPORT_BEN,
  POST_LAWSUIT_DETAIL,
  POST_LOAN_DB2_V2,
} from "../../../API/apiUrls";
import axios from "axios";
import {
  FileTextOutlined,
  BankOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarOutlined,
  HomeOutlined,
  PhoneOutlined,
  IdcardOutlined,
  FileWordOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { exportLawsuitToWord } from "../report/exportWord";

const { Text } = Typography;

const CopyableLawsuit = ({ open, close, dataDefault }) => {
  const [loading, setLoading] = useState(false);
  const [dataLawsuit, setDataLawsuit] = useState(null);
  const [dataLoadDB2, setDataLoadDB2] = useState(null);

  // ✅ โหลดข้อมูลทั้งหมด (DB2 + Lawsuit)
  const loadAllData = useCallback(async () => {
    if (!open || !dataDefault) return;

    setLoading(true);
    const controller = new AbortController();

    try {
      const [loanDB2Res, lawsuitRes] = await Promise.all([
        axios.post(
          POST_LOAN_DB2_V2,
          { [dataDefault.SCHEMA]: `'${dataDefault.CONTNO}'` },
          { headers: HEADERS_EXPORT_BEN, signal: controller.signal },
        ),
        axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: dataDefault.CONTNO,
            loanId: dataDefault.loanId,
            lawsuitId: dataDefault.lawsuitId || 0,
          },
          { headers: HEADERS_EXPORT_BEN, signal: controller.signal },
        ),
      ]);

      if (lawsuitRes?.data?.lawsuit) {
        setDataLawsuit(lawsuitRes.data.lawsuit);
      }

      if (loanDB2Res?.data?.[0]) {
        setDataLoadDB2(loanDB2Res.data[0]);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      }
    } finally {
      setLoading(false);
    }
  }, [open, dataDefault]);

  useEffect(() => {
    if (open) {
      loadAllData();
    }
  }, [open, loadAllData]);

  // ✅ Format Functions
  const formatDate = (date) => {
    if (!date) return "-";
    return dayjs(date).add(543, "year").format("D MMM YYYY");
  };

  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return "-";
    return Number(amount).toLocaleString("th-TH", { minimumFractionDigits: 2 });
  };

  // ✅ Component แสดง Text + Copyable
  const CopyableText = ({ label, value, icon }) => (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 16 }}>
        {icon} {label}
      </Text>
      <div>
        <Text
          strong
          copyable={value && value !== "-" ? { text: String(value) } : false}
          style={{ fontSize: 16 }}
        >
          {value || "-"}
        </Text>
      </div>
    </div>
  );

  // ✅ Component แสดง Money + Copyable
  const CopyableMoney = ({ label, value, icon }) => (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 16 }}>
        {icon} {label}
      </Text>
      <div>
        <Text
          strong
          copyable={value ? { text: String(value) } : false}
          style={{ fontSize: 16, color: "#52c41a" }}
        >
          {formatMoney(value)} บาท
        </Text>
      </div>
    </div>
  );

  // ✅ Component แสดงที่อยู่
  const AddressCard = ({ address, title }) => {
    const fullAddress = [
      address.ADDR1,
      address.ADDR2,
      address.MOOBAN,
      address.SOI,
      address.TUMB ? `ต.${address.TUMB}` : "",
      address.AUMPDES ? `อ.${address.AUMPDES}` : "",
      address.PROVDES ? `จ.${address.PROVDES}` : "",
      address.ZIP,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Card size="small" style={{ marginBottom: 8, background: "#fafafa" }}>
        <Row gutter={[16, 4]}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              <HomeOutlined /> {title}
            </Text>
            <div>
              <Text copyable={{ text: fullAddress }} style={{ fontSize: 16 }}>
                {fullAddress || "-"}
              </Text>
            </div>
          </Col>
          {address.TELP && (
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: 16 }}>
                <PhoneOutlined /> เบอร์โทร
              </Text>
              <div>
                <Text copyable style={{ fontSize: 16 }}>
                  {address.TELP}
                </Text>
              </div>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  // ✅ Columns สำหรับ workExpenseList
  const expenseColumns = [
    {
      title: "รายการ",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text copyable>{text}</Text>,
    },
    {
      title: "ยอดตั้งเบิก",
      dataIndex: "NETPAY",
      key: "NETPAY",
      align: "right",
      render: (amount) => (
        <Text copyable={{ text: amount }} style={{ color: "#1890ff" }}>
          {formatMoney(amount)}
        </Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      align: "center",
      render: (status) => (
        <Tag color={status === 1 ? "green" : "orange"}>
          {status === 1 ? "อนุมัติแล้ว" : "รออนุมัติ"}
        </Tag>
      ),
    },
  ];

  // ✅ Collapse Items สำหรับผู้ค้ำประกัน
  const guarantorItems =
    dataLoadDB2?.GUARANTORS?.map((guarantor, index) => ({
      key: `guarantor_${index}`,
      label: (
        <Space>
          <Tag color="green">ผู้ค้ำ {guarantor.GARNO}</Tag>
          <Text
            strong
            copyable={{
              text: ` ${guarantor.SNAM}
            ${guarantor.NAME1} ${guarantor.NAME2}`,
            }}
          >
            {guarantor.SNAM}
            {guarantor.NAME1} {guarantor.NAME2}
          </Text>
          <Tag color="orange">{guarantor.RELATN}</Tag>
        </Space>
      ),
      children: (
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12} md={8}>
            <CopyableText
              label="เลขบัตรประชาชน"
              value={guarantor.CUSCOD}
              icon={<IdcardOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <CopyableText
              label="วันเกิด"
              value={formatDate(guarantor.BIRTHDT)}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <CopyableText
              label="สัญชาติ"
              value={guarantor.NATION}
              icon={<UserOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <CopyableText
              label="อาชีพ"
              value={guarantor.OCCUP}
              icon={<UserOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <CopyableText
              label="สถานที่ทำงาน"
              value={guarantor.OFFIC}
              icon={<UserOutlined />}
            />
          </Col>
          <Col span={24}>
            <Divider style={{ margin: "8px 0" }} />
            <Text strong style={{ fontSize: 16 }}>
              <HomeOutlined /> ที่อยู่
            </Text>
          </Col>
          {guarantor.ADDRESS?.map((addr, addrIndex) => (
            <Col span={24} key={addrIndex}>
              <AddressCard
                address={addr}
                title={
                  addr.ADDRNO === "1" ? "ที่อยู่ทะเบียนบ้าน" : "ที่อยู่อื่น"
                }
              />
            </Col>
          ))}
        </Row>
      ),
    })) || [];

  return (
    <Modal
      title={
        <Row justify="space-between" align="middle" style={{ width: "95%" }}>
          <Space>
            <FileTextOutlined />
            <span>ข้อมูลสัญญา - {dataDefault?.CONTNO}</span>
          </Space>
          <Button
            variant="outlined"
            color="geekblue"
            icon={<FileWordOutlined />}
            onClick={() => exportLawsuitToWord(dataLoadDB2, dataLawsuit)}
            disabled={loading}
          >
            ดาวน์โหลด Word
          </Button>
        </Row>
      }
      open={open}
      onCancel={close}
      width={1000}
      footer={null}
    >
      <Spin spinning={loading}>
        <div style={{ maxHeight: "75vh", overflowY: "auto", padding: "0 8px" }}>
          {/* ============== ข้อมูลผู้เช่าซื้อ (CUSTOMER) ============== */}
          {dataLoadDB2?.CUSTOMER && (
            <Card
              size="small"
              title={
                <Space>
                  <UserOutlined />
                  <span>ข้อมูลผู้เช่าซื้อ</span>
                  <Tag color="blue">
                    <Text
                      copyable={{
                        text: `${dataLoadDB2.CUSTOMER.SNAM}${dataLoadDB2.CUSTOMER.NAME1} ${dataLoadDB2.CUSTOMER.NAME2}`,
                      }}
                    >
                      {dataLoadDB2.CUSTOMER.SNAM}
                      {dataLoadDB2.CUSTOMER.NAME1} {dataLoadDB2.CUSTOMER.NAME2}
                    </Text>
                  </Tag>
                </Space>
              }
              style={{ marginBottom: 12 }}
            >
              <Row gutter={[16, 4]}>
                <Col xs={24} sm={12} md={8}>
                  <CopyableText
                    label="เลขบัตรประชาชน"
                    value={dataLoadDB2.CUSTOMER.CUSCOD}
                    icon={<IdcardOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <CopyableText
                    label="วันเกิด"
                    value={formatDate(dataLoadDB2.CUSTOMER.BIRTHDT)}
                    icon={<CalendarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <CopyableText
                    label="สัญชาติ"
                    value={dataLoadDB2.CUSTOMER.NATION}
                    icon={<UserOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <CopyableText
                    label="อาชีพ"
                    value={dataLoadDB2.CUSTOMER.OCCUP}
                    icon={<UserOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <CopyableText
                    label="สถานที่ทำงาน"
                    value={dataLoadDB2.CUSTOMER.OFFIC}
                    icon={<UserOutlined />}
                  />
                </Col>
                <Col span={24}>
                  <Divider style={{ margin: "8px 0" }} />
                  <Text strong style={{ fontSize: 16 }}>
                    <HomeOutlined /> ที่อยู่
                  </Text>
                </Col>
                {dataLoadDB2.CUSTOMER.ADDRESS?.map((addr, index) => (
                  <Col span={24} key={index}>
                    <AddressCard
                      address={addr}
                      title={
                        addr.ADDRNO === "1"
                          ? "ที่อยู่ทะเบียนบ้าน"
                          : "ที่อยู่อื่น"
                      }
                    />
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* ============== ข้อมูลผู้ค้ำประกัน (GUARANTORS) ============== */}
          {dataLoadDB2?.GUARANTORS?.length > 0 && (
            <Card
              size="small"
              title={
                <Space>
                  <UserOutlined />
                  <span>ข้อมูลผู้ค้ำประกัน</span>
                  <Tag color="green">{dataLoadDB2.GUARANTORS.length} คน</Tag>
                </Space>
              }
              style={{ marginBottom: 12 }}
            >
              <Collapse
                items={guarantorItems}
                size="small"
                defaultActiveKey={guarantorItems.map((item) => item.key)}
              />
            </Card>
          )}

          {/* ============== ข้อมูลคดี (LAWSUIT) ============== */}
          {dataLawsuit && (
            <>
              <Divider>ข้อมูลคดี</Divider>

              <Card
                size="small"
                title={
                  <Space>
                    <BankOutlined />
                    <span>ข้อมูลคดี</span>
                  </Space>
                }
                style={{ marginBottom: 12 }}
              >
                <Row gutter={[16, 4]}>
                  <Col xs={24} sm={12} md={8}>
                    <CopyableText
                      label="ศาล"
                      value={dataLawsuit.provincialCourt}
                      icon={<BankOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <CopyableText
                      label="เลขคดีดำ"
                      value={dataLawsuit.blackCaseNumber}
                      icon={<FileTextOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <CopyableText
                      label="วันที่ยื่นฟ้อง"
                      value={formatDate(dataLawsuit.dateOfPlaint)}
                      icon={<CalendarOutlined />}
                    />
                  </Col>
                  <Col xs={24}>
                    <CopyableText
                      label="เรื่อง"
                      value={dataLawsuit.subject}
                      icon={<FileTextOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <CopyableText
                      label="วันนัดศาล"
                      value={formatDate(dataLawsuit.considerationDate)}
                      icon={<CalendarOutlined />}
                    />
                  </Col>
                </Row>
              </Card>

              {/* ข้อมูลทุนทรัพย์ */}
              <Card
                size="small"
                title={
                  <Space>
                    <DollarOutlined />
                    <span>ข้อมูลทุนทรัพย์</span>
                  </Space>
                }
                style={{ marginBottom: 12 }}
              >
                <Row gutter={[16, 4]}>
                  <Col xs={24} sm={12} md={6}>
                    <CopyableMoney
                      label="ทุนทรัพย์"
                      value={dataLawsuit.litigationFunds}
                      icon={<DollarOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <CopyableMoney
                      label="ค่าขาดประโยชน์"
                      value={dataLawsuit.lackOfBenefits}
                      icon={<DollarOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <CopyableMoney
                      label="ค่าติดตาม"
                      value={dataLawsuit.trackingFee}
                      icon={<DollarOutlined />}
                    />
                  </Col>
                </Row>
              </Card>

              {/* รายการค่าใช้จ่าย */}
              {dataLawsuit.workExpenseList?.length > 0 && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <FileTextOutlined />
                      <span>รายการค่าใช้จ่าย</span>
                      <Tag color="blue">
                        {dataLawsuit.workExpenseList.length} รายการ
                      </Tag>
                    </Space>
                  }
                  style={{ marginBottom: 12 }}
                >
                  <Table
                    dataSource={dataLawsuit.workExpenseList}
                    columns={expenseColumns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    summary={(data) => {
                      const total = data.reduce(
                        (sum, item) => sum + Number(item.NETPAY || 0),
                        0,
                      );
                      return (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong>รวม</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text
                              strong
                              copyable={{ text: String(total) }}
                              style={{ color: "#52c41a" }}
                            >
                              {formatMoney(total)}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} />
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </Card>
              )}

              {/* สถานะการดำเนินงาน */}
              {dataLawsuit.workLawsultStatus && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <ClockCircleOutlined />
                      <span>สถานะการดำเนินงาน</span>
                    </Space>
                  }
                >
                  <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                    <Descriptions.Item
                      label={
                        <Space>
                          {dataLawsuit.workLawsultStatus.acceptLawyer ? (
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          ) : (
                            <ClockCircleOutlined style={{ color: "#faad14" }} />
                          )}
                          <span>ทนายรับงาน</span>
                        </Space>
                      }
                    >
                      <Text
                        copyable={!!dataLawsuit.workLawsultStatus.acceptLawyer}
                      >
                        {formatDate(dataLawsuit.workLawsultStatus.acceptLawyer)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Space>
                          {dataLawsuit.workLawsultStatus.withdrawPayment ? (
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          ) : (
                            <ClockCircleOutlined style={{ color: "#faad14" }} />
                          )}
                          <span>ขอเบิก</span>
                        </Space>
                      }
                    >
                      <Text
                        copyable={
                          !!dataLawsuit.workLawsultStatus.withdrawPayment
                        }
                      >
                        {formatDate(
                          dataLawsuit.workLawsultStatus.withdrawPayment,
                        )}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Space>
                          {dataLawsuit.workLawsultStatus
                            .approvalWithdrawPayment ? (
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          ) : (
                            <ClockCircleOutlined style={{ color: "#faad14" }} />
                          )}
                          <span>อนุมัติเบิก</span>
                        </Space>
                      }
                    >
                      <Text
                        copyable={
                          !!dataLawsuit.workLawsultStatus
                            .approvalWithdrawPayment
                        }
                      >
                        {formatDate(
                          dataLawsuit.workLawsultStatus.approvalWithdrawPayment,
                        )}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}
            </>
          )}

          {/* ============== ข้อมูลสัญญา (LOAN) ============== */}
          {dataLoadDB2?.LOAN && (
            <Card
              size="small"
              title={
                <Space>
                  <FileTextOutlined />
                  <span>ข้อมูลสัญญา</span>
                </Space>
              }
              style={{ marginBottom: 12 }}
            >
              <Row gutter={[16, 4]}>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="เลขสัญญาปัจจุบัน"
                    value={dataLoadDB2.LOAN.CONTNO}
                    icon={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="เลขสัญญาเก่า"
                    value={dataLoadDB2.LOAN.ISSUNO}
                    icon={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="วันที่ทำสัญญา"
                    value={formatDate(dataLoadDB2.LOAN.SDATE)}
                    icon={<CalendarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="วันครบกำหนด"
                    value={formatDate(dataLoadDB2.LOAN.LDATE)}
                    icon={<CalendarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="ยอดกู้"
                    value={dataLoadDB2.LOAN.NCSHPRC}
                    icon={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="ยอดกู้รวมดอก"
                    value={dataLoadDB2.LOAN.TOTPRC}
                    icon={<DollarOutlined />}
                  />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="ค่างวด/เดือน"
                    value={dataLoadDB2.LOAN.TOT_UPAY}
                    icon={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="จำนวนงวด"
                    value={`${dataLoadDB2.LOAN.T_NOPAY} งวด`}
                    icon={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="งวดค้างชำระ"
                    value={`${dataLoadDB2.LOAN.EXP_FRM} - ${dataLoadDB2.LOAN.EXP_TO} (${dataLoadDB2.LOAN.EXP_PRD} งวด)`}
                    icon={<ClockCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="จ่ายมาทั้งหมด"
                    value={dataLoadDB2.LOAN.SMPAY}
                    icon={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="ยอดค้างชำระ"
                    value={dataLoadDB2.LOAN.EXP_AMT}
                    icon={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="ลูกหนี้อื่นๆ"
                    value={dataLoadDB2.LOAN.PAYAMTAROTHR}
                    icon={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="จ่ายล่าสุด"
                    value={formatDate(dataLoadDB2.LOAN.LPAYD)}
                    icon={<CalendarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableMoney
                    label="จ่ายล่าสุดจำนวน"
                    value={dataLoadDB2.LOAN.LPAYA}
                    icon={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* ============== ข้อมูลรถ (MORTGAGE) ============== */}
          {dataLoadDB2?.MORTGAGE && (
            <Card
              size="small"
              title={
                <Space>
                  <CarOutlined />
                  <span>ข้อมูลรถ</span>
                </Space>
              }
              style={{ marginBottom: 12 }}
            >
              <Row gutter={[16, 4]}>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="ยี่ห้อ"
                    value={dataLoadDB2.MORTGAGE.TYPE}
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="รุ่น/แบบ"
                    value={
                      dataLoadDB2.MORTGAGE.MODEL +
                      "/" +
                      dataLoadDB2.MORTGAGE.BAAB
                    }
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="ทะเบียน"
                    value={dataLoadDB2.MORTGAGE.REGNO}
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="สี"
                    value={dataLoadDB2.MORTGAGE.COLOR}
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="เลขตัวถัง"
                    value={dataLoadDB2.MORTGAGE.STRNO}
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="เลขเครื่อง"
                    value={dataLoadDB2.MORTGAGE.ENGNO}
                    icon={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="ปีผลิต"
                    value={dataLoadDB2.MORTGAGE.MANUYR}
                    icon={<CalendarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CopyableText
                    label="จดทะเบียน"
                    value={dataLoadDB2.MORTGAGE.DORECV}
                    icon={<HomeOutlined />}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* Empty State */}
          {!dataLoadDB2 && !dataLawsuit && !loading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Text type="secondary">ไม่พบข้อมูล</Text>
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default CopyableLawsuit;
